import { defineAsyncComponent } from 'vue';

import { toPlugin, type ComponentType, type Plugin } from '@bv/helpers/components';

import { audioComponentsArray } from './audio';
import { whatsappTemplateArray } from './bbc/whatsapp-template';
import { chartsArray } from './charts';
import { formArray } from './form';
import { voipArray } from './voip';
import { workflowArray } from './workflow';

export {
  BvWhatsappTemplateParser,
  BvWhatsappTemplatePicker,
  BvWhatsappTemplatePreview,
  whatsappTemplateArray,
  whatsappTemplate,
} from './bbc/whatsapp-template';
export {
  BvHeatmapChart,
  BvMetricList,
  BvMetricItem,
  BvChartWrapper,
  BvPieChart,
  BvStackedChart,
  BvLineChart,
  BvBreakdownChart,
  BvExportChart,
  chartsArray,
  charts,
} from './charts';
export type { BvBtnVariant } from './form';
export {
  BvTextInput,
  BvFileInput,
  BvTextarea,
  BvSearchInput,
  BvColorInput,
  BvTelInput,
  BvSelectInput,
  BvDateInput,
  BvCalendar,
  BvSwitch,
  BvTagInput,
  BvAttachInput,
  BvTimeInput,
  BvOtp,
  BvRadio,
  BvBusinessHours,
  BvBtn,
  BvTestButton,
  formArray,
  form,
} from './form';
export { BvCallWidget, BvCallDetails, BvDialPad, BvQuickDial, voipArray, voip } from './voip';
export { BvWorkflowBuilder, workflowArray, workflow } from './workflow';

export { default as BvView } from './BvView.vue';
export { default as BvAlert } from './alert/BvAlert.vue';
export { default as BvAvatar } from './avatar/BvAvatar.vue';
export { default as BvBox } from './box/BvBox.vue';
export { default as BvBreadcrumbs } from './breadcrumb/BvBreadcrumbs.vue';
export { default as BvStatCard } from './stat-card/BvStatCard.vue';
export { default as BvChip } from './chip/BvChip.vue';
export { default as BvEmptyState } from './empty-state/BvEmptyState.vue';
export { default as BvMenu } from './menu/BvMenu.vue';
export { default as BvModal } from './modal/BvModal.vue';
export { default as BvDeleteModal } from './modal/delete-modal/BvDeleteModal.vue';
export { default as BvStatusModal } from './modal/status-modal/BvStatusModal.vue';
export { useBvModal, type BvModalActions, type BvModalContext } from './modal/composables/modal-context';
export { default as BvTabs } from './tabs/BvTabs.vue';
export { default as BvTimer } from './timer/BvTimer.vue';
export { default as BvPoweredByBevatel } from './powered-by-bevatel/BvPoweredByBevatel.vue';
export { default as BvAppLogo } from './app-logo/BvAppLogo.vue';
export { default as BvCopyBtn } from './form/copy-btn/BvCopyBtn.vue';
export { default as BvTable } from './table/BvTable.vue';
export { default as BvTableActions } from './table/actions/BvTableActions.vue';
export { default as BvInfiniteScroll } from './infinite-scroll/BvInfiniteScroll.vue';
export { default as BvLoader } from './icons/loading/BvLoader.vue';
export { default as BvIcon } from './icon/BvIcon.vue';
export { default as BvTooltip } from './tooltip/BvTooltip.vue';
export { default as BvUpgradeState } from './upgrade-state/BvUpgradeState.vue';
export { default as BvCard } from './card/BvCard.vue';
export { createBvIcon } from './icon/partials/create-bv-icon';

export const uiComponentsArray: ComponentType[] = [
  {
    name: 'BvView',
    component: defineAsyncComponent(() => import('@bv/components/BvView.vue')),
  },
  {
    name: 'BvAlert',
    component: defineAsyncComponent(() => import('@bv/components/alert/BvAlert.vue')),
  },
  {
    name: 'BvAvatar',
    component: defineAsyncComponent(() => import('@bv/components/avatar/BvAvatar.vue')),
  },
  {
    name: 'BvBox',
    component: defineAsyncComponent(() => import('@bv/components/box/BvBox.vue')),
  },
  {
    name: 'BvBreadcrumbs',
    component: defineAsyncComponent(() => import('@bv/components/breadcrumb/BvBreadcrumbs.vue')),
  },
  {
    name: 'BvCard',
    component: defineAsyncComponent(() => import('@bv/components/card/BvCard.vue')),
  },
  {
    name: 'BvStatCard',
    component: defineAsyncComponent(() => import('@bv/components/stat-card/BvStatCard.vue')),
  },
  {
    name: 'BvChip',
    component: defineAsyncComponent(() => import('@bv/components/chip/BvChip.vue')),
  },
  {
    name: 'BvEmptyState',
    component: defineAsyncComponent(() => import('@bv/components/empty-state/BvEmptyState.vue')),
  },
  {
    name: 'BvMenu',
    component: defineAsyncComponent(() => import('@bv/components/menu/BvMenu.vue')),
  },
  {
    name: 'BvModal',
    component: defineAsyncComponent(() => import('@bv/components/modal/BvModal.vue')),
  },
  {
    name: 'BvDeleteModal',
    component: defineAsyncComponent(() => import('@bv/components/modal/delete-modal/BvDeleteModal.vue')),
  },
  {
    name: 'BvStatusModal',
    component: defineAsyncComponent(() => import('@bv/components/modal/status-modal/BvStatusModal.vue')),
  },
  {
    name: 'BvTabs',
    component: defineAsyncComponent(() => import('@bv/components/tabs/BvTabs.vue')),
  },
  {
    name: 'BvTimer',
    component: defineAsyncComponent(() => import('@bv/components/timer/BvTimer.vue')),
  },
  {
    name: 'BvTable',
    component: defineAsyncComponent(() => import('@bv/components/table/BvTable.vue')),
  },
  {
    name: 'BvTableActions',
    component: defineAsyncComponent(() => import('@bv/components/table/actions/BvTableActions.vue')),
  },
  {
    name: 'BvAppLogo',
    component: defineAsyncComponent(() => import('@bv/components/app-logo/BvAppLogo.vue')),
  },
  {
    name: 'BvInfiniteScroll',
    component: defineAsyncComponent(() => import('@bv/components/infinite-scroll/BvInfiniteScroll.vue')),
  },
  {
    name: 'BvPoweredByBevatel',
    component: defineAsyncComponent(() => import('@bv/components/powered-by-bevatel/BvPoweredByBevatel.vue')),
  },
  {
    name: 'BvLoader',
    component: defineAsyncComponent(() => import('@bv/components/icons/loading/BvLoader.vue')),
  },
  {
    name: 'BvIcon',
    component: defineAsyncComponent(() => import('@bv/components/icon/BvIcon.vue')),
  },
  {
    name: 'BvTooltip',
    component: defineAsyncComponent(() => import('@bv/components/tooltip/BvTooltip.vue')),
  },
  {
    name: 'BvUpgradeState',
    component: defineAsyncComponent(() => import('@bv/components/upgrade-state/BvUpgradeState.vue')),
  },
];

const componentsArray: ComponentType[] = [
  ...chartsArray,
  ...whatsappTemplateArray,
  ...formArray,
  ...uiComponentsArray,
  ...workflowArray,
  ...audioComponentsArray,
  ...voipArray,
];

export const components: Plugin<[]> = toPlugin(componentsArray);
